import * as React from "react";

import { twMerge } from "tailwind-merge";

import style from "./style.module.scss";

export interface Column<T> {
  key: string;
  title: string | React.ReactNode;
  dataIndex?: string;
  render?(_value: any, _record?: T): React.ReactNode;
  renderHeader?(_value: any): React.ReactNode;
  style?: React.CSSProperties;
  headerClassName?: string;
  className?: string;
  onClick?: () => void;
}

interface Props<T> {
  /**
   * Set the column for the table.
   */
  columns: Column<T>[];
  /**
   * Set the data for the table.
   */
  dataSource: T[];
  customTable?: boolean;
  tableClassName?: string;
  headRowClassName?: string;
  headCellClassName?: string;
  rowClassName?: string;
  cellClassName?: string;

  onRowClick?: (record: any, e: React.MouseEvent) => void;
  rowClassNameCallback?: (record: any) => string;
  cellClassNameCallback?: (record: any, col: Column<T>) => string;
  afterRowRenderCallback?: (record: any, rowIndex: number) => React.ReactNode;
}

class Table<T extends Record<string, any>> extends React.PureComponent<
  Props<T>
> {
  getDataValue = (record: T, dataIndex: string) => {
    const keys = dataIndex.split(".");
    return keys.reduce((acc, key) => {
      return acc ? acc[key] : "";
    }, record);
  };

  render() {
    const {
      columns,
      dataSource,
      customTable,
      tableClassName,
      headRowClassName,
      headCellClassName,
      rowClassName,
      rowClassNameCallback,
      cellClassName,
      cellClassNameCallback,
      onRowClick,
      afterRowRenderCallback,
    } = this.props;

    return (
      <table className={twMerge(!customTable && style.table, tableClassName)}>
        <tbody>
          <tr className={headRowClassName}>
            {columns.map((col: Column<T>, colIndex: number) => {
              const {
                key,
                title,
                style,
                onClick,
                renderHeader,
                headerClassName,
              } = col;
              return (
                <td
                  className={twMerge(headCellClassName, headerClassName)}
                  key={`${colIndex}:${key}`}
                  style={style || {}}
                  onClick={onClick}
                >
                  {renderHeader ? renderHeader(title) : title}
                </td>
              );
            })}
          </tr>
          {dataSource.map((record: T, rowIndex: number) => {
            return (
              <React.Fragment key={rowIndex}>
                <tr
                  className={twMerge(
                    rowClassName,
                    rowClassNameCallback?.(record)
                  )}
                  onClick={(e) => onRowClick?.(record, e)}
                >
                  {columns.map((col: Column<T>, colIndex: number) => {
                    const { dataIndex, render, style, title, key, className } =
                      col;
                    const value = dataIndex
                      ? this.getDataValue(record, dataIndex)
                      : title;
                    return (
                      <td
                        className={twMerge(
                          cellClassName,
                          cellClassNameCallback?.(record, col),
                          className
                        )}
                        key={`${rowIndex}:${colIndex}:${key}`}
                        style={style || {}}
                      >
                        {render ? render(value, record) : value}
                      </td>
                    );
                  })}
                </tr>
                {afterRowRenderCallback?.(record, rowIndex)}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    );
  }
}

export default Table;
